/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import Radio from 'backbone.radio';
import Ladda from 'ladda';

import OrderTypes from '@/enums/OrderTypes';
import downloadDocument from '@/util/downloadDocument';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import PdfCollectionView from '@/views/PdfCollectionView/PdfCollectionView';

import GenerateCustomerResponsibilitiesDocument from '../../services/GenerateCustomerResponsibilitiesDocument';
import HandleOrderSubmission from '../../services/HandleOrderSubmission';

import './CustomerResponsibilitiesModalView.css';
import tmplCustomerResponsibilitiesModalView from './CustomerResponsibilitiesModalView.hbs';

const CustomerResponsibilitiesModalView = View.extend({
  template: tmplCustomerResponsibilitiesModalView,

  className: 'customer-responsibilities-modal-view',

  ui: {
    prevBtn: '.prevBtn',
    downloadBtn: '.downloadBtn',
    submitBtn: '.submitBtn',
    signBtn: '.signBtn',
    nextBtn: '.nextBtn',
    assessmentRequested: '[name=assessmentRequested]',
    acknowledgment: '#acknowledgment',
    pdfContainer: '.pdf-container',
    disableSigning: '.disable-signing',
    showLastPage: '.show-last-page',
    showBeforeSignature: '.show-before-signature',
    showAfterSignature: '.show-after-signature'
  },

  regions: {
    pdfContainer: '@ui.pdfContainer'
  },

  events: {
    'click @ui.prevBtn': 'prevPage',
    'click @ui.downloadBtn': 'downloadSignedPdf',
    'click @ui.submitBtn': 'submitOrder',
    'click @ui.signBtn': 'signAcknowledgment',
    'click @ui.nextBtn': 'nextPage',
    'change @ui.assessmentRequested': 'refreshSignButton',
    'change @ui.acknowledgment': 'refreshSignButton'
  },

  templateContext() {
    return {
      isWideFormat: this.model.get('orderTypeId') === OrderTypes.WIDE
    };
  },

  onRender() {
    this.pdfCollectionView = new PdfCollectionView({
      collection: this.collection
    });
    this.showChildView('pdfContainer', this.pdfCollectionView);

    this.viewPage = _.debounce((() => {
      this.ui.nextBtn.prop('disabled', false);
      this.collection.at(this.page).set('viewed', true);
    }), 2000, { trailing: true });

    this.page = 0;
    this.onChangePage();
  },

  prevPage() {
    this.page--;
    this.onChangePage();
  },

  downloadSignedPdf() {
    downloadDocument(this.customerResponsibilitiesDocument);
  },

  async submitOrder() {
    const spinner = Ladda.create(this.ui.submitBtn[0]);
    spinner.start();

    try {
      const orderId = this.model.get('orderId');
      await HandleOrderSubmission(orderId);

      this.showSubmissionSuccess(orderId);
      this.triggerMethod('modal:close');
      Radio.channel('navigate').request('show:ext:dashboard', this.model.get('commandId'));
    } catch (err) {
      this.showSubmissionError(orderId);
    } finally {
      spinner.stop();
    }
  },

  async signAcknowledgment() {
    const spinner = Ladda.create(this.ui.signBtn[0]);
    spinner.start();
    this.ui.disableSigning.prop('disabled', true);

    try {
      const orderId = this.model.get('orderId');
      const assessmentRequested = this.ui.assessmentRequested.filter(':checked').val() === '1';
      this.customerResponsibilitiesDocument =
        await GenerateCustomerResponsibilitiesDocument(orderId, assessmentRequested);

      this.nextPage();
      this.model.get('markAcknowledgmentSigned')();
    } catch (err) {
      this.showPdfGenerationError();
    } finally {
      spinner.stop();
      this.ui.disableSigning.prop('disabled', false);
    }
  },

  nextPage() {
    this.page++;
    this.onChangePage();
  },

  refreshSignButton() {
    const checkedAcknowledgment = this.ui.acknowledgment.prop('checked');

    const isWideFormat = this.model.get('orderTypeId') === OrderTypes.WIDE;
    const assessmentValid = isWideFormat || this.ui.assessmentRequested.filter(':checked').val();

    if (assessmentValid && checkedAcknowledgment) {
      this.ui.signBtn.prop('disabled', false);
    } else {
      this.ui.signBtn.prop('disabled', true);
    }
  },

  onChangePage() {
    this.pdfCollectionView.setActiveIndex(this.page);
    this.viewPage.cancel();

    const signaturePageIndex = this.collection.length - 1

    if (0 < this.page && this.page <= signaturePageIndex) {
      this.ui.prevBtn.show();
    } else {
      this.ui.prevBtn.hide();
    }

    if (this.page < signaturePageIndex) {
      this.ui.nextBtn.show();

      if (this.collection.at(this.page).get('viewed')) {
        this.ui.nextBtn.prop('disabled', false);
      } else {
        this.ui.nextBtn.prop('disabled', true);
        this.viewPage();
      }
    } else {
      this.ui.nextBtn.hide();
    }

    if (this.page === signaturePageIndex) {
      this.ui.showLastPage.show();
      this.refreshSignButton();
    } else {
      this.ui.showLastPage.hide();
    }

    if (this.page > signaturePageIndex) {
      // Acknowledgment has been signed
      this.ui.showBeforeSignature.hide();
      this.ui.showAfterSignature.show();
    } else {
      this.ui.showBeforeSignature.show();
      this.ui.showAfterSignature.hide();
    }
  },

  showPdfGenerationError() {
    showErrorMessage({
      errorCode: 'generationError',
      errorTitle: 'Generation Error',
      errorMessage: `There was an error generating the signed Customer Responsibilities Acknowledgment. Please try
        again or contact an administrator if the issue persists.`
    });
  },

  showSubmissionSuccess(orderId) {
    showSuccessMessage({
      code: 'submissionSuccess',
      title: 'Submit Success',
      message: `Order ${orderId} has been submitted.`,
      hideAfter: 5
    });
  },

  showSubmissionError(orderId) {
    showErrorMessage({
      errorCode: 'submissionError',
      errorTitle: 'Submission Error',
      errorMessage: `There was an error submitting order ${orderId}. Please try again or contact an administrator if
        the issue persists.`
    });
  }
});

export default CustomerResponsibilitiesModalView;
